import { useState, useRef, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import './TimePicker.css';

type TimePickerProps = {
    onChange: (value: Dayjs | null) => void;
    value: Dayjs | null;
    minuteStep?: number;
};

export default function TimePicker({ onChange, value, minuteStep = 1 }: TimePickerProps) {
    const [open, setOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const hourColumnRef = useRef<HTMLDivElement>(null);
    const minuteColumnRef = useRef<HTMLDivElement>(null);

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i).filter((m) => m % minuteStep === 0);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll selected hour and minute to top when panel opens
    useEffect(() => {
        if (open) {
            setTimeout(() => {
                if (hourColumnRef.current && value?.hour() !== undefined) {
                    const hourCell = hourColumnRef.current.querySelector(`[data-hour="${value.hour()}"]`);
                    if (hourCell) {
                        hourColumnRef.current.scrollTop = (hourCell as HTMLElement).offsetTop;
                    }
                }

                if (minuteColumnRef.current && value?.minute() !== undefined) {
                    const minuteCell = minuteColumnRef.current.querySelector(`[data-minute="${value.minute()}"]`);
                    if (minuteCell) {
                        minuteColumnRef.current.scrollTop = (minuteCell as HTMLElement).offsetTop;
                    }
                }
            }, 0);
        }
    }, [open, value]);

    const handleSelect = (type: 'hour' | 'minute' | 'second', val: number) => {
        let newValue: Dayjs;

        if (value) {
            newValue = value.set(type, val);
        } else {
            newValue = dayjs().hour(0).minute(0).second(0).set(type, val);
        }

        onChange(newValue);

        setTimeout(() => {
            if (type === 'hour' && hourColumnRef.current) {
                const hourCell = hourColumnRef.current.querySelector(`[data-hour="${val}"]`);
                if (hourCell) {
                    hourColumnRef.current.scrollTop = (hourCell as HTMLElement).offsetTop;
                }
            } else if (type === 'minute' && minuteColumnRef.current) {
                const minuteCell = minuteColumnRef.current.querySelector(`[data-minute="${val}"]`);
                if (minuteCell) {
                    minuteColumnRef.current.scrollTop = (minuteCell as HTMLElement).offsetTop;
                }
            }
        }, 0);
    };

    return (
        <div className="timepicker-container">
            <input
                type="text"
                readOnly
                onClick={() => setOpen(!open)}
                value={
                    value ? `${String(value.hour()).padStart(2, '0')}:${String(value.minute()).padStart(2, '0')}` : ''
                }
                placeholder="Select time"
                className="input input-modal input-modal-time-picker"
            />

            {open && (
                <div className="timepicker-panel" ref={panelRef}>
                    <div className="timepicker-column" ref={hourColumnRef}>
                        {hours.map((h) => (
                            <div
                                key={h}
                                data-hour={h}
                                className={`timepicker-cell ${value?.hour() === h ? 'selected' : ''}`}
                                onClick={() => handleSelect('hour', h)}
                            >
                                {String(h).padStart(2, '0')}
                            </div>
                        ))}
                        <div className="timepicker-spacer" /> {/* spacer */}
                    </div>

                    <div className="timepicker-column" ref={minuteColumnRef}>
                        {minutes.map((m) => (
                            <div
                                key={m}
                                data-minute={m}
                                className={`timepicker-cell ${value?.minute() === m ? 'selected' : ''}`}
                                onClick={() => handleSelect('minute', m)}
                            >
                                {String(m).padStart(2, '0')}
                            </div>
                        ))}
                        <div className="timepicker-spacer" /> {/* spacer */}
                    </div>
                </div>
            )}
        </div>
    );
}
